/**
 * AgedCareWise AI chat — client side. Talks to OUR backend (/rest/ai/chat), which holds the Gemini key
 * server-side and calls the provider. The API key is NEVER in the browser (see server/ + .env.example).
 */
import axios from 'axios';
import type { AxiosError } from 'axios';
import { getAccessToken } from './authClient.ts';
import { getStoredProfileId } from './profileApi.ts';
import { apiBaseUrl } from './apiBase.ts';

interface GeminiErrorBody {
  error?: { message?: string; code?: string };
}

/** One prior turn of the chat, as replayed to the AI for context. */
export interface GeminiChatTurn {
  sender: 'user' | 'bot';
  text: string;
}

export interface SendMessageToGeminiOptions {
  /** Latest user message. */
  message: string;
  systemInstruction: string;
  /** Prior turns only. */
  conversationHistory?: GeminiChatTurn[];
}

/** API base URL: the backend origin. Configured via REACT_APP_API_BASE_URL; defaults to local dev. */
function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

export function formatGeminiFailureForUser(err: AxiosError<GeminiErrorBody>): string {
  const status = err.response?.status;
  const apiMsg = String(err.response?.data?.error?.message || '').trim();
  const lower = apiMsg.toLowerCase();
  const code = err.response?.data?.error?.code;

  if (code === 'AI_ANON_QUOTA_EXCEEDED') {
    return "You've used today's free questions — they reset tomorrow. Or create a free account (it takes a minute) and keep chatting with AWise.";
  }
  if (code === 'AI_QUOTA_EXCEEDED') {
    return "You've used today's AI assistant allowance — it resets tomorrow. Everything else in AgedCareWise keeps working as normal.";
  }
  if (status === 429 || lower.includes('quota') || lower.includes('resource exhausted') || code === 'RESOURCE_EXHAUSTED') {
    return 'The AI service is busy or a short-term usage limit was hit. Wait a minute and try again — this often happens after several questions in a row.';
  }
  if (!err.response) {
    return "Couldn't reach the AI service (network). Check your connection, then try again.";
  }
  if (status === 401) {
    return 'Please sign in to use the AI assistant — it’s included with your account.';
  }
  if (status === 503 || code === 'NOT_CONFIGURED') {
    return 'AI chat is temporarily unavailable (the service is not configured). Please try again later.';
  }
  if (status === 504) {
    return 'The AI took too long to respond. Please try again in a moment.';
  }
  if (status === 403) {
    return 'The AI service rejected the request. This is a server configuration issue, not something you did — please try again later.';
  }
  if (status === 400 && apiMsg) {
    const short = apiMsg.length > 220 ? `${apiMsg.slice(0, 217)}…` : apiMsg;
    return `The request wasn't accepted: ${short}`;
  }
  if (apiMsg && apiMsg.length < 300) {
    return `Sorry — ${apiMsg}`;
  }
  return "Sorry, I couldn't get a response just then. Please try again in a moment.";
}

export async function sendMessageToGemini({
  message,
  systemInstruction,
  conversationHistory = [],
}: SendMessageToGeminiOptions): Promise<string> {
  const url = `${getApiBaseUrl()}/rest/ai/chat`;
  try {
    // The server requires a signed-in caller (it spends real money per request). Attach the
    // session token when accounts are configured; demo/local builds have none and stay open.
    const token = await getAccessToken().catch(() => null);
    const headers: { 'Content-Type': string; Authorization?: string } = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    // Which file is open, so AWise's affordability tool reports on the right one. Sent, not trusted:
    // the server serves it only if this user OWNS that file, and resolves it from ownership alone when
    // absent — which it is for a single-file caller, who is never given an id in the first place.
    const profileId = getStoredProfileId();
    const response = await axios.post(
      url,
      { message, systemInstruction, conversationHistory, ...(profileId ? { profileId } : {}) },
      { headers, timeout: 95_000 },
    );
    const text = response.data?.text;
    if (text) return text;
    // Server returns an empty text for a blocked/empty provider reply.
    return "I couldn't generate a reply for that message. Try rephrasing, or ask about aged care, retirement, super, or related topics.";
  } catch (err) {
    const axiosErr = err as AxiosError<GeminiErrorBody>;
    if (process.env.NODE_ENV === 'development') {
      console.warn('[AgedCareWise AI]', axiosErr.response?.status || 'no-status', axiosErr.response?.data?.error?.message || axiosErr.message || axiosErr);
    }
    return formatGeminiFailureForUser(axiosErr);
  }
}
